// 9fbef606107a605d69c0edbcd8029e5d
import logger from '@tcp/core/src/utils/loggerInstance';
import endpoints from '@tcp/core/src/services/endpoints';
import { executeExternalAPICall, initiateApiCall } from '@tcp/core/src/services/handler';
import mediaQuickView from '@tcp/core/src/services/abstractors/mediaQuickView';
import {
  getStyliticsUserName,
  getApiTimeOut,
  isCanada,
  getProductSmallImageUrl,
} from '@tcp/core/src/utils/utils';

const Abstractor = {
  getData: async (params) => {
    try {
      const { isRedirectToPDP, uniqueKey } = params;
      const response = await Abstractor.fetchStyliticsData(params);
      if (!response) return [];

      const styliticsData = response.body;

      if (isRedirectToPDP) {
        return Abstractor.processStyliticsData(styliticsData, uniqueKey);
      }

      const productIds = Abstractor.extractProductIds(styliticsData);
      if (!productIds || productIds.length === 0) {
        return [];
      }
      const inventoryResponse = await mediaQuickView.getData({ productIds });

      return Abstractor.processData(styliticsData, inventoryResponse?.products || {}, uniqueKey);
    } catch (error) {
      Abstractor.handleError(error);
      return [];
    }
  },

  fetchStyliticsData: async ({ count = 1, tags, uniqueKey }) => {
    const {
      getStyliticsProductsByTag: { method, URI, clientTimeout, serverTimeout },
    } = endpoints;

    const payload = {
      body: {
        username: getStyliticsUserName(),
        min: 1,
        total: count,
        tags,
      },
      webService: {
        method,
        URI,
        reqTimeout: getApiTimeOut({ serverTimeout, clientTimeout }),
      },
    };

    const response = await executeExternalAPICall(payload);
    if (
      !response?.body ||
      !Array.isArray(response.body) ||
      response.body.some((item) => !item.items)
    ) {
      logger.error(
        `Stylitics API returned an empty or malformed response for ${uniqueKey}`,
        response
      );
      return null;
    }
    return response;
  },

  extractProductIds: (items) => {
    return items.flatMap((item) => item.items.map(({ remote_id: remoteId }) => remoteId));
  },

  processData: (styliticsData, inventoryData) => {
    return styliticsData.map((item) => Abstractor.processItem(inventoryData)(item));
  },

  processStyliticsData: (styliticsData) => {
    return styliticsData.map(({ large_image_url: largeImageUrl, id, items: subItems }) => {
      const subItemsId = Abstractor.getSubItemsId(subItems);
      return {
        id,
        largeImageUrl,
        subItemsId,
        items: subItems,
      };
    });
  },

  processItem:
    (inventoryData) =>
    ({ id, items: subItems = [], large_image_url: largeImageUrl = '' }) => {
      const items = Abstractor.mapSubItems(subItems, inventoryData);
      const outOfStockItems = subItems
        .filter(({ remote_id: remoteId }) => !inventoryData[remoteId]?.inventoryCount)
        .map(({ remote_id: remoteId }) => remoteId)
        .join(', ');

      return {
        id,
        largeImageUrl,
        items,
        isOutOfStock: items.length === 0,
        outOfStockItemsId: outOfStockItems,
      };
    },

  mapSubItems: (subItems, inventoryData = {}) => {
    const subItemsId = Abstractor.getSubItemsId(subItems);
    const mappedItems = subItems.map(
      ({
        small_image_url: smallImageUrl,
        name,
        remote_id: remoteId,
        price: productPrice,
        sale_price: discountedPrice,
        brand,
      }) => ({
        smallImageUrl,
        name,
        remoteId,
        subItemsId,
        productPrice,
        discountedPrice,
        inventoryCount: inventoryData?.[remoteId]?.inventoryCount || 0,
        brand,
      })
    );

    return inventoryData ? mappedItems.filter((item) => item.inventoryCount > 0) : mappedItems;
  },

  getSubItemsId: (subItems) => subItems.map(({ remote_id: remoteId }) => remoteId).join('-'),

  handleError: (e, uniqueKey) => {
    logger.error(`Error in Shop by Look Stylitics Abstractor: ${uniqueKey}`, e);
  },

  getUnbxdData: ({ productIds, uniqueKey, xappURLConfig }) => {
    // Convert A1-A2-A3__B1-B2-B3 format to A1,A2,A3,B1,B2,B3
    const formattedProductIds = productIds
      .replace(/__/g, '-') // Replace double underscore with hyphen
      .replace(/-/g, ','); // Replace all hyphens with commas

    const fields = `productId,totalInventory,seo_token,productimage,TCPPrimaryBrandUSStore,TCPPrimaryBrandCanadaStore,min_list_price,min_offer_price,product_name`;
    const payload = {
      body: {
        variants: true,
        'variants.count': 100,
        'variants.fields': 'v_qty',
        version: 'V2',
        pagetype: 'boolean',
        id: formattedProductIds,
        promotion: false,
        fields,
      },
      webService: endpoints && endpoints.getProductDetails,
    };
    return initiateApiCall(payload, null, xappURLConfig)
      .then((response) => Abstractor.processUnbxdData(response, productIds, uniqueKey))
      .catch((e) => Abstractor.handleError(e, uniqueKey));
  },

  processUnbxdData: (res, productIds, uniqueKey) => {
    const productDetails = Abstractor.validateUnbxdResponse(res, uniqueKey);
    if (!productDetails) {
      return [
        {
          id: '',
          isOutOfStock: true,
          items: [],
        },
      ];
    }

    // Handle null/undefined productIds
    if (!productIds || (Array.isArray(productDetails) && productDetails?.length === 0)) {
      return [
        {
          id: '',
          isOutOfStock: true,
          items: [],
        },
      ];
    }

    // Split by double underscore to get groups
    const productGroups = productIds.split('__');
    // Process each group separately
    return productGroups.map((group) => {
      // Convert group format A1-A2-A3 or A1,A2,A3 to array [A1, A2, A3]
      const productIdsArray = group.split(/[,-]/).map((id) => id.trim());

      const items = Abstractor.processProductGroup(productIdsArray, productDetails);

      const isOutOfStock = Abstractor.checkGroupOutOfStock(productIdsArray, productDetails);

      return {
        id: productIdsArray.join('-'), // Group ID like A1-A2-A3
        isOutOfStock,
        items,
      };
    });
  },

  validateUnbxdResponse: (res, uniqueKey) => {
    if (!res?.body || !res.body.response || !Array.isArray(res.body.response.products)) {
      logger.error(`Unbxd API returned an empty or malformed response for ${uniqueKey}`, res);
      return null;
    }
    return res.body.response.products;
  },

  processProductGroup: (productIdsArray, productDetails) => {
    return productIdsArray.map((productId) => {
      const matchedProduct = Abstractor.findMatchingProduct(productId, productDetails);
      const id = matchedProduct.generalProductId || matchedProduct.productId || productId;

      return {
        brand: !isCanada()
          ? matchedProduct.TCPPrimaryBrandUSStore
          : matchedProduct.TCPPrimaryBrandCanadaStore || '',
        discountedPrice: matchedProduct.min_offer_price || matchedProduct.offerPrice || 0,
        inventoryCount: matchedProduct.totalInventory || 0,
        name: matchedProduct.product_name || '',
        productPrice: matchedProduct.min_list_price || matchedProduct.listPrice || 0,
        remoteId: id,
        smallImageUrl: getProductSmallImageUrl(id),
      };
    });
  },

  findMatchingProduct: (productId, productDetails) => {
    return (
      productDetails.find(
        (product) => product.uniqueId === productId || product.productId === productId
      ) || {}
    );
  },

  checkGroupOutOfStock: (productIdsArray, productDetails) => {
    return productIdsArray.every((productId) => {
      const matchedProduct = Abstractor.findMatchingProduct(productId, productDetails);
      return !matchedProduct || matchedProduct.totalInventory === 0;
    });
  },
};

export default Abstractor;
